import {
  all,
  call,
  put,
  takeEvery,
  cancelled,
  select,
  delay,
} from "redux-saga/effects";
import { SagaIterator } from "redux-saga";
import { JsonApiResponse } from "@stemscopes/api";
import axios from "axios";
import { endpoints } from "configs";
import { request, generatePath } from "utils";
import * as actions from "./actions";
import * as Types from "./types";
import {
  wrapWords,
  applyCoordinates,
  getHighlightedElements,
  paintElements,
  getSelectionObject,
  detectSelection,
  removeSelectionRange,
  redrawSelection,
  removeSelection,
  addCommentIcon,
  getCommentIcon,
  removeCommentIcon,
  scrollToElement,
  hasSeveralComments,
  getDuplicated,
} from "./utils";
import {
  getActiveSelection,
  getSelections,
  sortByTime,
  getIntersections,
} from "./selectors";
import { Highlight } from "./types";

function* fetchHighlights({
  payload: { filter },
}: ReturnType<typeof actions.fetchHighlights.request>): SagaIterator {
  const source = axios.CancelToken.source();
  try {
    const {
      data: { data },
    }: JsonApiResponse<Types.FetchedHighlights> = yield call(
      request.get,
      filter.studentAssignmentId
        ? endpoints.studentAssignmentHighlights
        : endpoints.highlights,
      {
        params: filter.studentAssignmentId
          ? {
              studentAssignmentId: filter.studentAssignmentId,
            }
          : {
              filter,
            },
        cancelToken: source.token,
      },
    );

    const isFetchNotForCache = window.location.pathname.includes(
      String(filter.studentAssignmentId || ""),
    );
    if (isFetchNotForCache) {
      yield put(
        actions.fetchHighlights.success({
          data: data.highlights
            ? [...data.highlights, ...(data.studentAssignmentHighlights || [])]
            : data,
        }),
      );
      yield put(actions.initSelections());
    }
  } catch (error) {
    yield put(actions.fetchHighlights.failure(undefined, undefined));
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

function* createHighlight({
  payload: highlight,
}: ReturnType<typeof actions.createHighlight.request>): SagaIterator {
  try {
    const teacherId = yield select((state) => state.auth.data.profile.id);
    const filter = yield select((state) => state.highlighting.filter);

    yield put(actions.hideCommentTooltip());

    if (highlight.comment && highlight.color === "transparent") {
      const intersections = yield select(getIntersections, highlight);
      const hasIntersection = intersections.length;
      if (hasIntersection) {
        intersections.forEach((value: Types.Highlight) =>
          redrawSelection(value),
        );
      }
    }

    const response: JsonApiResponse<Types.Highlight> = yield call(
      request.post,
      filter.studentAssignmentId
        ? endpoints.studentAssignmentHighlights
        : endpoints.highlights,
      {
        ...highlight,
        ...filter,
        ...(filter.studentAssignmentId ? {} : { teacherId }),
      },
    );

    yield put(
      actions.createHighlight.success({
        data: response.data.data,
      }),
    );

    if (highlight.comment) {
      yield call(addCommentIcon, response.data.data);
      yield put(actions.showCommentTooltip());
    }
  } catch (error) {
    yield put(actions.createHighlight.failure(undefined, undefined));
  }
}

function* updateHighlight({
  payload: highlight,
}: ReturnType<typeof actions.updateHighlight.request>): SagaIterator {
  try {
    const filter = yield select((state) => state.highlighting.filter);
    const response: JsonApiResponse<Types.Highlight> = yield call(
      request.put,
      generatePath(
        filter.studentAssignmentId
          ? endpoints.studentAssignmentHighlight
          : endpoints.highlight,
        { id: highlight.id },
      ),
      highlight,
    );

    yield put(
      actions.updateHighlight.success({
        data: response.data.data,
      }),
    );
  } catch (error) {
    yield put(actions.updateHighlight.failure(undefined, undefined));
  }
}

function* removeHighlight({
  payload: highlight,
}: ReturnType<typeof actions.removeHighlight.request>): SagaIterator {
  try {
    if (!highlight.id || (highlight.id && !highlight.comment)) {
      yield call(removeSelection, highlight);
      yield put(actions.removeHighlight.success(highlight.id));
    }

    if (highlight.id) {
      if (highlight.comment) {
        const transparentSelection: Highlight = {
          ...highlight,
          color: "transparent",
        };
        yield call(redrawSelection, transparentSelection);
        yield put(actions.updateHighlight.request(transparentSelection));
      } else {
        const filter = yield select((state) => state.highlighting.filter);
        yield call(
          request.delete,
          generatePath(
            filter.studentAssignmentId
              ? endpoints.studentAssignmentHighlight
              : endpoints.highlight,
            { id: highlight.id },
          ),
        );
        yield put(actions.removeHighlight.success(highlight.id));
        const selections = yield select((state) =>
          sortByTime(getSelections(state)),
        );
        selections.forEach(
          (value: Types.Highlight) =>
            value.color && value.id && redrawSelection(value),
        );
      }
    }
  } catch (error) {
    yield put(actions.removeHighlight.failure(undefined, undefined));
  }
}

function* initHighlighting({
  payload: { filter, lang },
}: ReturnType<typeof actions.initHighlighting>): SagaIterator {
  yield delay(50);
  yield call(wrapWords(lang));
  yield call(applyCoordinates);
  yield put(actions.fetchHighlights.request({ filter }));
}

function* initSelections(): SagaIterator {
  const selections = yield select((state) => sortByTime(getSelections(state)));

  for (let i = 0; i < selections.length; i += 1) {
    const selection = selections[i];
    const $elements = yield call(getHighlightedElements, selection);
    if ($elements) {
      if (selection.color) {
        yield call(
          paintElements,
          $elements,
          selection.color,
          selection.readonly,
        );
      }
      if (selection.comment) {
        yield call(addCommentIcon, selection);
      }

      const highlightItemId = new URLSearchParams(window.location.search).get(
        "highlightItem",
      );

      if (highlightItemId === selection.id) {
        yield call(scrollToElement, $elements[0]);
      }
    }
  }
}

function* handleMouseUp({
  payload: { left, top },
}: ReturnType<typeof actions.handleMouseUp>): SagaIterator {
  const selections = yield select((state) => state.highlighting.selections);
  let text = "";
  let startAtPoint = null;
  let endAtPoint = null;
  let selection: Highlight | null | undefined = null;

  const selectionObj: Selection | null = window.getSelection();
  const isCollapsed = !!window.getSelection()?.isCollapsed;

  let startPoint: number;
  let endPoint: number;
  let sequence: number;
  let contains = null;

  // Will check whether intersection detected or not
  let intersection: HTMLElement | null = null;

  if (typeof selectionObj?.containsNode !== "function") {
    return null;
  }

  $(".highlight-wrapper").each((key, item) => {
    contains = selectionObj?.containsNode(item, true);
    if (contains) {
      const $item = $(item);
      text += $item.text();
      sequence = parseInt($item.attr("data-sequence") || "");
      if ($item.hasClass("highlighted")) {
        intersection = item;
      }
      if (sequence <= startPoint || !startPoint) {
        startPoint = sequence;
        startAtPoint = item;
      }
      if (sequence >= endPoint || !endPoint) {
        endPoint = sequence;
        endAtPoint = item;
      }
    }
  });

  const duplicated = getDuplicated(startAtPoint, endAtPoint, selections);

  if (
    startAtPoint &&
    endAtPoint &&
    !isCollapsed &&
    (!intersection || (intersection && !duplicated))
  ) {
    selection = getSelectionObject({ startAtPoint, endAtPoint, text });
    redrawSelection(selection);
    const validSelections = selections.filter((v: Types.Highlight) => v.id);
    yield put(actions.setSelections(validSelections.concat(selection)));
  }
  if (
    intersection &&
    isCollapsed &&
    $(":hover").last().hasClass("highlighted")
  ) {
    selection = detectSelection($(intersection), selections);
  }

  if (intersection && !isCollapsed && duplicated) {
    selection = duplicated;
  }

  if (selection) {
    yield put(actions.setActiveSelectionId(selection.id));
    yield put(actions.setTooltipData({ isShown: true, left, top }));

    removeSelectionRange();
  }
}

function* handleMouseDown(): SagaIterator {
  const {
    tooltip: { isShown: isTooltipShown },
    commentTooltip: { isShown: isCommentTooltipShown },
  } = yield select((state) => state.highlighting);
  const selections = yield select((state) => sortByTime(getSelections(state)));

  if (isTooltipShown) {
    yield put(
      actions.setTooltipData({
        isShown: false,
      }),
    );
  }

  if (isCommentTooltipShown) {
    yield put(actions.hideCommentTooltip());
  }

  if (!isTooltipShown) {
    return;
  }

  for (let i = 0; i < selections.length; i += 1) {
    const selection = selections[i];
    const isCommentIcon =
      (getCommentIcon(selection) as HTMLElement)?.dataset?.highlightId ===
      String(selection.id);

    if (isCommentIcon && !selection.comment) {
      removeCommentIcon(selection);
    }

    if (
      (selection.color === "transparent" && !selection.comment) ||
      selection.color === "lightblue"
    ) {
      yield put(actions.removeHighlight.request(selection));

      selections.forEach(
        (v: Types.Highlight) => v.color && v.id && redrawSelection(v),
      );
    }
  }
}

function* editComment({
  payload: selection,
}: ReturnType<typeof actions.editComment>): SagaIterator {
  yield put(actions.setActiveSelectionId(selection.id));
  const commentIcon = yield call(addCommentIcon, selection);

  if (commentIcon) {
    yield put(actions.setCommentTooltipData({ isShown: true, mode: "edit" }));
  }
}

function* removeComment({
  payload: selection,
}: ReturnType<typeof actions.removeComment>): SagaIterator {
  if (!selection.color) {
    yield put(actions.removeHighlight.request({ ...selection, comment: null }));
  } else {
    yield put(actions.updateHighlight.request({ ...selection, comment: null }));
  }
  const selections = yield select((state) => sortByTime(getSelections(state)));
  // preventing removing comment icon when there's readonly comment for the same selection
  if (!hasSeveralComments(selections, selection)) {
    yield call(removeCommentIcon, selection);
  }
  // redrawing comments after clear all comments
  selections.forEach((v: Highlight) => v.color && redrawSelection(v));
}

function* updateActiveSelection({
  payload: data,
}: ReturnType<typeof actions.updateActiveSelection>): SagaIterator {
  const selection = yield select(getActiveSelection);
  const result = { ...selection, ...data };
  yield call(redrawSelection, result);

  if (!result.color) {
    // redrawing selection after lightblue select
    const selections = yield select((state) =>
      sortByTime(getSelections(state)),
    );
    selections.forEach(
      (value: Types.Highlight) =>
        value.color && value.id && redrawSelection(value),
    );
  }
  if (result.id) {
    yield put(actions.updateHighlight.request(result));
  } else {
    yield put(
      actions.createHighlight.request({
        ...result,
      }),
    );
  }
}

export default function* rootSaga(): SagaIterator {
  yield all([
    takeEvery(actions.fetchHighlights.request, fetchHighlights),
    takeEvery(actions.createHighlight.request, createHighlight),
    takeEvery(actions.updateHighlight.request, updateHighlight),
    takeEvery(actions.removeHighlight.request, removeHighlight),
    takeEvery(actions.initHighlighting, initHighlighting),
    takeEvery(actions.initSelections, initSelections),
    takeEvery(actions.handleMouseUp, handleMouseUp),
    takeEvery(actions.handleMouseDown, handleMouseDown),
    takeEvery(actions.editComment, editComment),
    takeEvery(actions.removeComment, removeComment),
    takeEvery(actions.updateActiveSelection, updateActiveSelection),
  ]);
}
